import { GlButton, GlForm } from '@gitlab/ui';
import illustration from '@gitlab/svgs/dist/illustrations/empty-state/empty-key-md.svg?url';
import csrf from '~/lib/utils/csrf';
import { createAlert } from '~/alert';
import { supported, convertGetParams, convertGetResponse } from '~/authentication/webauthn/util';
import { WEBAUTHN_AUTHENTICATE } from '~/authentication/webauthn/constants';
import WebAuthnError from '~/authentication/webauthn/error';
import VerificationLayout from './verification_layout.vue';
import VerificationDivider from './verification_divider.vue';
import VerificationRecoverAccount from './verification_recover_account.vue';
export default {
    name: 'WebAuthnAuthentication',
    components: {
        GlButton,
        GlForm,
        VerificationLayout,
        VerificationDivider,
        VerificationRecoverAccount
    },
    props: {
        path: {
            type: String,
            required: true
        },
        rememberMe: {
            type: String,
            required: true
        },
        rememberMeEnabled: {
            type: Boolean,
            required: true
        },
        webauthnParams: {
            type: Object,
            required: true
        },
        totpEnabled: {
            type: Boolean,
            required: true
        },
        emailEnabled: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'switch-method',
        'webauthn-not-supported'
    ],
    data () {
        return {
            // Lets us cancel an in-flight credentials.get() on unmount.
            abortController: new AbortController(),
            inProgress: false,
            deviceResponse: ''
        };
    },
    mounted () {
        if (supported()) {
            this.authenticate();
        } else {
            this.$emit('webauthn-not-supported');
        }
    },
    destroyed () {
        this.abortController.abort();
        this.alert?.dismiss();
    },
    methods: {
        tryAgain () {
            this.authenticate();
        },
        async authenticate () {
            // Dismiss the previous error before a new attempt so alerts don't stack.
            this.alert?.dismiss();
            this.inProgress = true;
            try {
                const opts = {
                    publicKey: convertGetParams(this.webauthnParams),
                    signal: this.abortController.signal
                };
                const response = await navigator.credentials.get(opts);
                this.deviceResponse = JSON.stringify(convertGetResponse(response));
                // Let Vue write deviceResponse into the hidden input before the native submit reads it.
                await this.$nextTick();
                // Keep inProgress true through submit: submit() only starts the navigation, so the page
                // is still here and a second credentials.get() would raise a fresh prompt mid-request.
                this.$refs.form.$el.submit();
            } catch (err) {
                // A get() we aborted ourselves (unmount / method switch) is intentional; surfacing
                // it would leave a stray "(AbortError)" alert behind from the destroyed instance.
                if (this.abortController.signal.aborted) return;
                this.inProgress = false;
                const webAuthnError = new WebAuthnError(err, WEBAUTHN_AUTHENTICATE);
                // preservePrevious so a client-side device error doesn't wipe a server-rendered
                // flash (e.g. "Authentication via WebAuthn device failed") in the same container.
                this.alert = createAlert({
                    message: `${webAuthnError.message()} (${webAuthnError.errorName})`,
                    preservePrevious: true
                });
            }
        }
    },
    csrf,
    illustration
};
